import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { useAuth } from "../auth/AuthContext";
import Select from "../components/Select";

const API = process.env.REACT_APP_API_URL;
const STORAGE_KEY = "scenathon_initial_submission_page_v1";

// Validation checklist function
async function validateInitialSubmission(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { defval: "" });

        const results = {
          currentTrendPathway: false,
          currentTrendPathwayValue: "—",
          calibrationYear2020: false,
          calibrationYear2020Value: "—",
          tradeAdjustmentNo: false,
          tradeAdjustmentNoValue: "—",
          populationActivityLow: false,
          populationActivityLowValue: "—",
          climateChangeLow: false,
          climateChangeLowValue: "—",
          globalWarmingAR6: false,
          globalWarmingAR6Value: "—",
          reportingAggregateNoErrors: false,
          reportingByproductNoErrors: false,
          pathwaysTableJson: null, // Store entire PATHWAYS table
        };

        // Helper function to check for error values in worksheet cells
        const hasErrorValues = (worksheet) => {
          if (!worksheet) return true;
          const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
          for (let row = range.s.r; row <= range.e.r; row++) {
            for (let col = range.s.c; col <= range.e.c; col++) {
              const cellAddr = XLSX.utils.encode_cell({ r: row, c: col });
              const cell = worksheet[cellAddr];
              if (cell) {
                const cellValue = String(cell.v || "").trim();
                if (cellValue === "#DIV/0!" || cellValue === "#N/A" || cellValue.includes("#DIV/0!") || cellValue.includes("#N/A")) {
                  return true;
                }
              }
            }
          }
          return false;
        };

        // Extract PATHWAYS selection table via backend endpoint
        // This will be done asynchronously after the basic validation
        
        // Check SCENATHON_report sheet for REPORTING_AGGREGATE and REPORTING_BYPRODUCT tables
        const scenathonReportSheetName = workbook.SheetNames.find(
          name => name.toLowerCase() === "scenathon_report"
        );
        const scenathonReportSheet = scenathonReportSheetName ? workbook.Sheets[scenathonReportSheetName] : null;
        if (scenathonReportSheet) {
          // Check for error values in the sheet (both tables are in the same sheet)
          const hasErrors = hasErrorValues(scenathonReportSheet);
          
          // Since both tables are in the same sheet, we check if there are errors
          // We assume no errors means both tables are clean
          results.reportingAggregateNoErrors = !hasErrors;
          results.reportingByproductNoErrors = !hasErrors;
        } else {
          // If sheet doesn't exist, mark as error
          results.reportingAggregateNoErrors = false;
          results.reportingByproductNoErrors = false;
        }

        resolve(results);
      } catch (err) {
        console.error("Error validating file:", err);
        resolve({
          currentTrendPathway: false,
          currentTrendPathwayValue: "—",
          calibrationYear2020: false,
          calibrationYear2020Value: "—",
          tradeAdjustmentNo: false,
          tradeAdjustmentNoValue: "—",
          populationActivityLow: false,
          populationActivityLowValue: "—",
          climateChangeLow: false,
          climateChangeLowValue: "—",
          globalWarmingAR6: false,
          globalWarmingAR6Value: "—",
          reportingAggregateNoErrors: false,
          reportingByproductNoErrors: false,
          pathwaysTableJson: null,
        });
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
}) {
  return (
    <Select
      label={label}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full"
    />
  );
}

function readStoredState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeStoredState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export default function InitialSubmissionPage() {
  const { accessToken, csrfToken, logout, user, selectedScenathon } = useAuth();

  // Determine user roles
  const isAdmin = user?.type_id === 1;
  const userCountry = user?.country;

  const [excelFile, setExcelFile] = useState(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [sendScenathonId, setSendScenathonId] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [scenathons, setScenathons] = useState([]);
  const [participatingCountries, setParticipatingCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loading, setLoading] = useState(true);
  const [existingFile, setExistingFile] = useState(null);
  const [loadingExistingFile, setLoadingExistingFile] = useState(false);
  const [deletingFile, setDeletingFile] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [validatingFile, setValidatingFile] = useState(false);
  const [showPathwaysPreview, setShowPathwaysPreview] = useState(false);
  const [animatedItemsCount, setAnimatedItemsCount] = useState(0);
  const [isValidationReportExpanded, setIsValidationReportExpanded] = useState(true);

  const apiFetch = useCallback(async (url, options = {}) => {
    const headers = {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
      ...(options.headers || {}),
    };

    const res = await fetch(url, {
      credentials: "include",
      ...options,
      headers,
    });

    if (res.status === 401) {
      logout?.();
      throw new Error("Session expired");
    }

    return res;
  }, [accessToken, csrfToken, logout]);

  const handleFileSelect = useCallback(async (file) => {
    if (!file) {
      setExcelFile(null);
      setValidationResults(null);
      return;
    }

    setExcelFile(file);
    setValidatingFile(true);

    try {
      const results = await validateInitialSubmission(file);
      
      // Extract PATHWAYS table from backend endpoint
      try {
        const formData = new FormData();
        formData.append("file", file);
        
        const pathwaysResponse = await fetch(`${API}/api/scenathon/extract-pathways-table`, {
          method: "POST",
          body: formData,
        });
        
        if (pathwaysResponse.ok) {
          const pathwaysResult = await pathwaysResponse.json();
          console.log("🔍 PATHWAYS EXTRACTION RESULT:", pathwaysResult);
          
          if (pathwaysResult.success && pathwaysResult.data && pathwaysResult.data.length > 0) {
            results.pathwaysTableJson = pathwaysResult.data;
            
            console.log(`📊 Extracted ${pathwaysResult.data.length} rows`);
            console.log("📋 First row keys:", Object.keys(pathwaysResult.data[0]));
            console.log("📝 First row data:", pathwaysResult.data[0]);
            
            // Extract selected row info for validation
            const selectedRow = pathwaysResult.data.find(row => {
              // Look for "SELECTION" column (case-insensitive, with or without spaces)
              const keys = Object.keys(row);
              const selectionKey = keys.find(k => k.toUpperCase().includes("SELECTION"));
              if (selectionKey) {
                const val = row[selectionKey];
                return val === "x" || val === "X";
              }
              return false;
            }) || pathwaysResult.data[0];
            
            console.log("✅ Selected row:", selectedRow);
            
            if (selectedRow) {
              const findColumn = (row, ...possibleNames) => {
                const rowKeys = Object.keys(row);
                console.log(`  🔎 Looking for: ${possibleNames.join(", ")} in keys: ${rowKeys.join(", ")}`);
                
                for (const name of possibleNames) {
                  const found = rowKeys.find(key => key.toUpperCase() === name.toUpperCase());
                  if (found && row[found] !== undefined && row[found] !== null && String(row[found]).trim() !== "") {
                    console.log(`    ✓ Found exact match: "${found}" = "${row[found]}"`);
                    return row[found];
                  }
                }
                for (const name of possibleNames) {
                  const found = rowKeys.find(key => key.toUpperCase().includes(name.toUpperCase()));
                  if (found && row[found] !== undefined && row[found] !== null && String(row[found]).trim() !== "") {
                    console.log(`    ✓ Found partial match: "${found}" = "${row[found]}"`);
                    return row[found];
                  }
                }
                console.log(`    ✗ No match found`);
                return null;
              };
              
              const pathway = findColumn(selectedRow, "PATHWAY");
              const pathwayStr = pathway ? String(pathway).trim() : "";
              results.currentTrendPathwayValue = pathwayStr || "—";
              // Normalize: remove spaces and compare (handles "Current Trends", "CurrentTrends", "current trends", etc.)
              results.currentTrendPathway = pathway && pathway.toString().toLowerCase().replace(/\s+/g, "") === "currenttrends";
              
              const calibYear = findColumn(selectedRow, "Calibration Year", "S0");
              const calibYearStr = calibYear ? String(calibYear).trim() : "";
              results.calibrationYear2020Value = calibYearStr || "—";
              results.calibrationYear2020 = calibYear && String(calibYear).trim() === "2020";
              
              const tradeAdj = findColumn(selectedRow, "S12", "Trade adjustment");
              const tradeAdjStr = tradeAdj ? String(tradeAdj).trim() : "";
              results.tradeAdjustmentNoValue = tradeAdjStr || "—";
              results.tradeAdjustmentNo = tradeAdj && String(tradeAdj).trim().toLowerCase() === "no";
              
              const populationLevel = findColumn(selectedRow, "S13", "Level of activity", "population activity");
              const populationLevelStr = populationLevel ? String(populationLevel).trim() : "";
              results.populationActivityLowValue = populationLevelStr || "—";
              results.populationActivityLow = populationLevel && String(populationLevel).trim().toLowerCase() === "low";
              
              const climateChange = findColumn(selectedRow, "S14", "Climate change");
              const climateChangeStr = climateChange ? String(climateChange).trim() : "";
              results.climateChangeLowValue = climateChangeStr || "—";
              results.climateChangeLow = climateChange && String(climateChange).trim().toLowerCase() === "nochange";
              
              const globalWarming = findColumn(selectedRow, "S19", "Global warming");
              const globalWarmingStr = globalWarming ? String(globalWarming).trim() : "";
              results.globalWarmingAR6Value = globalWarmingStr || "—";
              results.globalWarmingAR6 = globalWarming && String(globalWarming).trim() === "AR6";
            }
          } else {
            console.warn("⚠️ No data returned from PATHWAYS extraction or success=false");
          }
        } else {
          console.warn("❌ Could not extract PATHWAYS table from backend:", pathwaysResponse.status);
        }
      } catch (pathwaysErr) {
        console.warn("Error extracting PATHWAYS table:", pathwaysErr);
      }
      
      setValidationResults(results);
    } catch (err) {
      console.error("Error validating file:", err);
      setValidationResults(null);
    } finally {
      setValidatingFile(false);
    }
  }, []);

  const handleExcelUpload = useCallback(async () => {
    if (!excelFile) {
      Swal.fire({
        icon: "warning",
        title: "No file selected",
        text: "Please select an Excel file to upload.",
      });
      return;
    }

    if (!sendScenathonId || !selectedCountry) {
      Swal.fire({
        icon: "warning",
        title: "Missing information",
        text: "Please select both Scenathon and Country before uploading.",
      });
      return;
    }

    // Check if file already exists and ask for confirmation
    if (existingFile) {
      const confirmed = await Swal.fire({
        icon: "warning",
        title: "File already exists",
        text: `"${existingFile.filename}" already exists. Uploading will replace it.`,
        showCancelButton: true,
        confirmButtonText: "Replace",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#54B046",
      });

      if (!confirmed.isConfirmed) return;
    }

    setUploadingExcel(true);
    try {
      // Get scenathon from scenathons array
      const scenathon = scenathons.find(
        (s) => String(s.id) === String(sendScenathonId)
      );
      
      if (!scenathon) {
        throw new Error("Selected scenathon not found");
      }

      // Get country from participatingCountries array
      const country = participatingCountries.find(
        (c) => c.country_code === selectedCountry
      );

      if (!country) {
        throw new Error("Selected country not found");
      }

      // Construct file name: scenathon_country with original extension
      const fileExtension = excelFile.name.substring(excelFile.name.lastIndexOf('.'));
      const fileName = `${scenathon.name || scenathon.title}_${country.country_code}${fileExtension}`;

      console.log("[1/1] Uploading file to cloud via backend...");
      const formData = new FormData();
      formData.append("file", excelFile, fileName);
      formData.append("scenathonId", sendScenathonId);
      formData.append("country", selectedCountry);

      const headers = {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
      };

      const res = await fetch(`${API}/api/scenathon/upload-to-cloud`, {
        method: "POST",
        body: formData,
        headers,
        credentials: "include",
      });

      if (res.status === 401) {
        logout?.();
        throw new Error("Session expired");
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error || err.message || `Upload failed: HTTP ${res.status}`);
      }

      const data = await res.json();

      if (!data.success || !data.downloadUrl) {
        throw new Error(data.error || "Upload failed: no download URL received");
      }

      console.log("✓ File uploaded successfully");

      Swal.fire({
        icon: "success",
        title: "File uploaded successfully",
        html: `<div style="text-align: left;">
          <p><strong>File:</strong> ${fileName}</p>
          <p><strong>Country:</strong> ${country.country_code}</p>
          <p><strong>Scenathon:</strong> ${scenathon.name || scenathon.title}</p>
          <p><strong>Download URL:</strong></p>
          <p style="word-break: break-all; font-size: 0.85em; color: #0066cc; font-family: monospace;">
            ${data.downloadUrl}
          </p>
        </div>`,
      });

      setExcelFile(null);
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = "";

      // Refresh existing file info
      setExistingFile({
        filename: fileName,
        downloadUrl: data.downloadUrl,
        fileSize: null, // Will be updated on next check
      });
    } catch (err) {
      console.error("Error uploading file:", err);
      Swal.fire({
        icon: "error",
        title: "Upload failed",
        html: `<div style="text-align: left;">
          <p><strong>Error:</strong> ${err.message}</p>
          <p style="margin-top: 10px; font-size: 0.9em; color: #666;">
            Make sure:
            <ul>
              <li>File format is correct (CSV, XLSX)</li>
              <li>Contains SCENATHON_report worksheet</li>
              <li>Network connection is stable</li>
            </ul>
          </p>
        </div>`,
      });
    } finally {
      setUploadingExcel(false);
    }
  }, [excelFile, selectedCountry, sendScenathonId, scenathons, participatingCountries, existingFile, accessToken, csrfToken, logout]);

  // Load scenathons
  useEffect(() => {
    let alive = true;

    async function loadPageData() {
      setLoading(true);
      try {
        const scRes = await apiFetch(`${API}/api/scenathon/scenathons`);

        if (!scRes.ok) throw new Error(`Scenathons HTTP ${scRes.status}`);

        const scData = await scRes.json();

        if (!alive) return;

        setScenathons(Array.isArray(scData?.scenathons) ? scData.scenathons : []);
      } catch (err) {
        console.error(err);
        if (!alive) return;

        Swal.fire({
          icon: "error",
          title: "Could not load the page",
          text: err.message || "Error loading scenathons.",
        });
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadPageData();

    return () => {
      alive = false;
    };
  }, [apiFetch]);

  // Auto-load user's scenathon for non-admin users
  useEffect(() => {
    if (!isAdmin && selectedScenathon?.id) {
      console.log("Auto-selecting user's scenathon:", selectedScenathon.id);
      setSendScenathonId(String(selectedScenathon.id));
    }
  }, [isAdmin, selectedScenathon]);

  // For non-admin users, force sendScenathonId to always be their selectedScenathon
  useEffect(() => {
    if (!isAdmin && selectedScenathon?.id && sendScenathonId !== String(selectedScenathon.id)) {
      console.log("Forcing sendScenathonId to:", selectedScenathon.id);
      setSendScenathonId(String(selectedScenathon.id));
    }
  }, [isAdmin, selectedScenathon]);

  // Load participating countries
  useEffect(() => {
    if (!sendScenathonId) {
      setParticipatingCountries([]);
      setSelectedCountry("");
      return;
    }

    const loadCountries = async () => {
      setLoadingCountries(true);
      try {
        const res = await apiFetch(`${API}/api/scenathon/scenathons/${sendScenathonId}/participating-countries`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && Array.isArray(data.countries)) {
            setParticipatingCountries(data.countries);
            
            // Auto-select country based on role
            if (!isAdmin && userCountry) {
              // Non-admin: check if userCountry is in this scenathon's participating countries
              const userCountryExists = data.countries.find(c => c.country_code === userCountry);
              if (userCountryExists) {
                setSelectedCountry(userCountry);
                console.log("Setting non-admin country to:", userCountry);
              } else {
                // User's country not in this scenathon, use first available
                if (data.countries.length > 0) {
                  setSelectedCountry(data.countries[0].country_code);
                  console.warn("User country", userCountry, "not in scenathon. Using", data.countries[0].country_code);
                }
              }
            } else if (isAdmin && data.countries.length > 0) {
              // Admin: first country
              setSelectedCountry(data.countries[0].country_code);
              console.log("Setting admin country to:", data.countries[0].country_code);
            }
          }
        }
      } catch (err) {
        console.error("Error loading participating countries:", err);
      } finally {
        setLoadingCountries(false);
      }
    };

    loadCountries();
  }, [sendScenathonId, isAdmin, userCountry, apiFetch]);

  // For non-admin users, force selectedCountry to always be userCountry
  useEffect(() => {
    if (!isAdmin && userCountry && selectedCountry !== userCountry) {
      console.log("Forcing selectedCountry to:", userCountry);
      setSelectedCountry(userCountry);
    }
  }, [isAdmin, userCountry]);

  // Check for existing file
  useEffect(() => {
    if (!sendScenathonId || !selectedCountry) {
      setExistingFile(null);
      return;
    }

    let alive = true;

    async function checkFile() {
      setLoadingExistingFile(true);
      try {
        const scenathon = scenathons.find(
          (s) => String(s.id) === String(sendScenathonId)
        );
        if (!scenathon) return;

        const fileExtension = excelFile?.name?.substring(excelFile.name.lastIndexOf('.')) || '.xlsx';
        const fileName = `${scenathon.name || scenathon.title}_${selectedCountry}${fileExtension}`;

        const res = await apiFetch(
          `${API}/api/scenathon/check-cloud-file?country=${selectedCountry}&filename=${encodeURIComponent(fileName)}`
        );

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        if (!alive) return;

        if (data.success && data.exists) {
          setExistingFile({
            filename: fileName,
            downloadUrl: data.downloadUrl,
            fileSize: data.fileSize,
          });
        } else {
          setExistingFile(null);
        }
      } catch (err) {
        console.error("Error checking file:", err);
        if (alive) setExistingFile(null);
      } finally {
        if (alive) setLoadingExistingFile(false);
      }
    }

    checkFile();

    return () => {
      alive = false;
    };
  }, [sendScenathonId, selectedCountry, scenathons, apiFetch]);

  const handleDeleteFile = useCallback(async () => {
    if (!existingFile || !selectedCountry) return;

    const confirmed = await Swal.fire({
      icon: "warning",
      title: "Delete existing file?",
      text: `Are you sure you want to delete "${existingFile.filename}"?`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!confirmed.isConfirmed) return;

    setDeletingFile(true);
    try {
      const res = await apiFetch(`${API}/api/scenathon/delete-cloud-file`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          country: selectedCountry,
          filename: existingFile.filename,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      if (data.success) {
        Swal.fire({
          icon: "success",
          title: "File deleted",
          text: "Existing file has been deleted successfully.",
        });
        setExistingFile(null);
      } else {
        throw new Error(data.error || "Delete failed");
      }
    } catch (err) {
      console.error("Error deleting file:", err);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text: err.message || "Error deleting file",
      });
    } finally {
      setDeletingFile(false);
    }
  }, [existingFile, selectedCountry, apiFetch]);

  const handleDownloadFile = useCallback(() => {
    if (!existingFile?.downloadUrl) return;
    window.open(existingFile.downloadUrl, "_blank");
  }, [existingFile]);

  const scenathonOptions = useMemo(
    () =>
      scenathons.map((s) => ({
        value: String(s.id),
        label: s.name || s.title || `Scenathon ${s.id}`,
      })),
    [scenathons],
  );

  // Animate validation items appearing one by one
  useEffect(() => {
    if (validationResults && !validatingFile) {
      setAnimatedItemsCount(0);
      let itemIndex = 0;
      const totalItems = 8; // 6 checks + 2 error checks
      
      const animationInterval = setInterval(() => {
        if (itemIndex < totalItems) {
          setAnimatedItemsCount(itemIndex + 1);
          itemIndex++;
        } else {
          clearInterval(animationInterval);
        }
      }, 150); // 150ms delay between each item
      
      return () => clearInterval(animationInterval);
    } else if (validatingFile) {
      setAnimatedItemsCount(0);
    }
  }, [validationResults, validatingFile]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="rounded-2xl border border-[#E5E7EB] bg-white px-6 py-4 text-sm text-[#6B7280] shadow-sm">
          Loading scenathons...
        </div>
      </div>
    );
  }

  return (
    <div className="mt-9">
      <main className="mx-auto py-8 px-6 min-h-screen">
        <div className="rounded-3xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-8">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-1 h-8 bg-gradient-to-b from-[#54B046] to-emerald-500 rounded-full"></div>
              <h2 className="text-3xl font-bold text-slate-900">
                Upload Initial FABLE-C
              </h2>
            </div>
            <p className="mt-3 text-slate-600 ml-4">
              Upload your initial submission data from an Excel file with SCENATHON_report worksheet.
            </p>
          </div>

          <div className="mb-8 grid gap-6 lg:grid-cols-4">
            <div className="space-y-2">
              <SelectField
                label="Scenathon"
                value={sendScenathonId || ""}
                onChange={isAdmin ? setSendScenathonId : undefined}
                options={scenathonOptions}
                placeholder="Select scenathon"
                disabled={!isAdmin}
              />
            </div>

            {sendScenathonId && (
              <div className="space-y-2">
                <SelectField
                  label="Country"
                  value={selectedCountry}
                  onChange={isAdmin ? setSelectedCountry : undefined}
                  options={participatingCountries.map(item => ({
                    value: item.country_code,
                    label: item.country_code
                  }))}
                  placeholder="Select country"
                  disabled={loadingCountries || participatingCountries.length === 0 || !isAdmin}
                />
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6">
              {sendScenathonId && (
                <div>
                  <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-3">
                    Upload Initial Calculator
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                      disabled={uploadingExcel || validatingFile}
                      className="flex-1 block w-full rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-900 placeholder-slate-500 focus:border-[#54B046] focus:outline-none focus:ring-2 focus:ring-[#54B046]/20 disabled:bg-slate-100 disabled:text-slate-500 cursor-pointer"
                    />
                    <button
                      onClick={handleExcelUpload}
                      disabled={uploadingExcel || !excelFile || validatingFile}
                      className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#54B046] to-emerald-500 px-6 py-2 font-semibold text-white shadow-md hover:shadow-lg transition-shadow disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed"
                    >
                      {uploadingExcel ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Update Calculator
                        </>
                      )}
                    </button>
                  </div>
                  {excelFile && (
                    <p className="mt-2 text-sm text-slate-600">
                      Selected: <span className="font-semibold text-slate-900">{excelFile.name}</span>
                    </p>
                  )}

                  {validatingFile && (
                    <div className="mt-4 flex items-center gap-2 text-sm text-slate-700">
                      <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Validating file...
                    </div>
                  )}

                  {validationResults && !validatingFile && (
                    <div className="mt-6 rounded-xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-white shadow-sm p-6">
                      {/* Header with progress indicator */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <button
                            type="button"
                            onClick={() => setIsValidationReportExpanded(!isValidationReportExpanded)}
                            className="flex items-center gap-2 mb-1 hover:opacity-75 transition-opacity"
                          >
                            <svg 
                              className={`w-5 h-5 text-emerald-600 transform transition-transform ${isValidationReportExpanded ? 'rotate-0' : '-rotate-90'}`}
                              fill="currentColor" 
                              viewBox="0 0 20 20"
                            >
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            <div className="w-1 h-6 bg-gradient-to-b from-emerald-500 to-green-500 rounded-full"></div>
                            <h4 className="text-lg font-bold text-slate-900">Validation Report</h4>
                          </button>
                          <p className="text-sm text-slate-600 ml-8">FABLE-C Initial Submission Checklist</p>
                        </div>
                        {/* Progress badge */}
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <div className="text-2xl font-bold text-emerald-600">
                              {[validationResults.currentTrendPathway, validationResults.calibrationYear2020, validationResults.tradeAdjustmentNo, validationResults.populationActivityLow, validationResults.climateChangeLow, validationResults.globalWarmingAR6, validationResults.reportingAggregateNoErrors, validationResults.reportingByproductNoErrors].filter(Boolean).length}
                            </div>
                            <div className="text-xs text-slate-600">of 8 passed</div>
                          </div>
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center">
                            <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {isValidationReportExpanded && (
                        <>
                          {/* Pathway Configuration Section */}
                          <div className="mb-6">
                            <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wide mt-6 mb-3 ml-1">Pathway Configuration (6 items)</h5>
                            <ul className="space-y-2">
                          {animatedItemsCount >= 1 && (
                            <li className="flex items-start gap-3 animate-fade-in animate-slide-in-left p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                              <span className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${validationResults.currentTrendPathway ? "bg-emerald-100" : "bg-red-100"}`}>
                                {validationResults.currentTrendPathway ? (
                                  <svg className="w-3 h-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                ) : (
                                  <span className="text-red-600 text-xs font-bold">✕</span>
                                )}
                              </span>
                              <div className="flex-1 min-w-0">
                                <span className={`text-sm font-medium ${validationResults.currentTrendPathway ? "text-slate-900" : "text-slate-700"}`}>Pathway = Current Trends</span>
                                {!validationResults.currentTrendPathway && (
                                  <div className="mt-1 text-xs text-red-600 font-medium">
                                    Found: <span className="font-mono">"{validationResults.currentTrendPathwayValue}"</span>
                                  </div>
                                )}
                              </div>
                              <span className={`flex-shrink-0 text-xs font-bold ${validationResults.currentTrendPathway ? "text-emerald-600" : "text-red-600"}`}>
                                {validationResults.currentTrendPathway ? "✓" : "✗"}
                              </span>
                            </li>
                          )}
                          {animatedItemsCount >= 2 && (
                            <li className="flex items-start gap-3 animate-fade-in animate-slide-in-left p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                              <span className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${validationResults.calibrationYear2020 ? "bg-emerald-100" : "bg-red-100"}`}>
                                {validationResults.calibrationYear2020 ? (
                                  <svg className="w-3 h-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                ) : (
                                  <span className="text-red-600 text-xs font-bold">✕</span>
                                )}
                              </span>
                              <div className="flex-1 min-w-0">
                                <span className={`text-sm font-medium ${validationResults.calibrationYear2020 ? "text-slate-900" : "text-slate-700"}`}>Calibration Year = 2020</span>
                                {!validationResults.calibrationYear2020 && (
                                  <div className="mt-1 text-xs text-red-600 font-medium">
                                    Found: <span className="font-mono">"{validationResults.calibrationYear2020Value}"</span>
                                  </div>
                                )}
                              </div>
                              <span className={`flex-shrink-0 text-xs font-bold ${validationResults.calibrationYear2020 ? "text-emerald-600" : "text-red-600"}`}>
                                {validationResults.calibrationYear2020 ? "✓" : "✗"}
                              </span>
                            </li>
                          )}
                          {animatedItemsCount >= 3 && (
                            <li className="flex items-start gap-3 animate-fade-in animate-slide-in-left p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                              <span className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${validationResults.tradeAdjustmentNo ? "bg-emerald-100" : "bg-red-100"}`}>
                                {validationResults.tradeAdjustmentNo ? (
                                  <svg className="w-3 h-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                ) : (
                                  <span className="text-red-600 text-xs font-bold">✕</span>
                                )}
                              </span>
                              <div className="flex-1 min-w-0">
                                <span className={`text-sm font-medium ${validationResults.tradeAdjustmentNo ? "text-slate-900" : "text-slate-700"}`}>Trade Adjustment = No</span>
                                {!validationResults.tradeAdjustmentNo && (
                                  <div className="mt-1 text-xs text-red-600 font-medium">
                                    Found: <span className="font-mono">"{validationResults.tradeAdjustmentNoValue}"</span>
                                  </div>
                                )}
                              </div>
                              <span className={`flex-shrink-0 text-xs font-bold ${validationResults.tradeAdjustmentNo ? "text-emerald-600" : "text-red-600"}`}>
                                {validationResults.tradeAdjustmentNo ? "✓" : "✗"}
                              </span>
                            </li>
                          )}
                          {animatedItemsCount >= 4 && (
                            <li className="flex items-start gap-3 animate-fade-in animate-slide-in-left p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                              <span className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${validationResults.populationActivityLow ? "bg-emerald-100" : "bg-red-100"}`}>
                                {validationResults.populationActivityLow ? (
                                  <svg className="w-3 h-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                ) : (
                                  <span className="text-red-600 text-xs font-bold">✕</span>
                                )}
                              </span>
                              <div className="flex-1 min-w-0">
                                <span className={`text-sm font-medium ${validationResults.populationActivityLow ? "text-slate-900" : "text-slate-700"}`}>Population Activity = Low</span>
                                {!validationResults.populationActivityLow && (
                                  <div className="mt-1 text-xs text-red-600 font-medium">
                                    Found: <span className="font-mono">"{validationResults.populationActivityLowValue}"</span>
                                  </div>
                                )}
                              </div>
                              <span className={`flex-shrink-0 text-xs font-bold ${validationResults.populationActivityLow ? "text-emerald-600" : "text-red-600"}`}>
                                {validationResults.populationActivityLow ? "✓" : "✗"}
                              </span>
                            </li>
                          )}
                          {animatedItemsCount >= 5 && (
                            <li className="flex items-start gap-3 animate-fade-in animate-slide-in-left p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                              <span className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${validationResults.climateChangeLow ? "bg-emerald-100" : "bg-red-100"}`}>
                                {validationResults.climateChangeLow ? (
                                  <svg className="w-3 h-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                ) : (
                                  <span className="text-red-600 text-xs font-bold">✕</span>
                                )}
                              </span>
                              <div className="flex-1 min-w-0">
                                <span className={`text-sm font-medium ${validationResults.climateChangeLow ? "text-slate-900" : "text-slate-700"}`}>Climate Change = NoChange</span>
                                {!validationResults.climateChangeLow && (
                                  <div className="mt-1 text-xs text-red-600 font-medium">
                                    Found: <span className="font-mono">"{validationResults.climateChangeLowValue}"</span>
                                  </div>
                                )}
                              </div>
                              <span className={`flex-shrink-0 text-xs font-bold ${validationResults.climateChangeLow ? "text-emerald-600" : "text-red-600"}`}>
                                {validationResults.climateChangeLow ? "✓" : "✗"}
                              </span>
                            </li>
                          )}
                          {animatedItemsCount >= 6 && (
                            <li className="flex items-start gap-3 animate-fade-in animate-slide-in-left p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                              <span className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${validationResults.globalWarmingAR6 ? "bg-emerald-100" : "bg-red-100"}`}>
                                {validationResults.globalWarmingAR6 ? (
                                  <svg className="w-3 h-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                ) : (
                                  <span className="text-red-600 text-xs font-bold">✕</span>
                                )}
                              </span>
                              <div className="flex-1 min-w-0">
                                <span className={`text-sm font-medium ${validationResults.globalWarmingAR6 ? "text-slate-900" : "text-slate-700"}`}>Global Warming = AR6</span>
                                {!validationResults.globalWarmingAR6 && (
                                  <div className="mt-1 text-xs text-red-600 font-medium">
                                    Found: <span className="font-mono">"{validationResults.globalWarmingAR6Value}"</span>
                                  </div>
                                )}
                              </div>
                              <span className={`flex-shrink-0 text-xs font-bold ${validationResults.globalWarmingAR6 ? "text-emerald-600" : "text-red-600"}`}>
                                {validationResults.globalWarmingAR6 ? "✓" : "✗"}
                              </span>
                            </li>
                          )}
                        </ul>
                      </div>

                      {/* Reporting Tables Section */}
                      <div className="border-t border-slate-200 pt-4">
                        <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3 ml-1">Reporting Tables (2 items)</h5>
                        <ul className="space-y-2">
                          {animatedItemsCount >= 7 && (
                            <li className="flex items-start gap-3 animate-fade-in animate-slide-in-left p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                              <span className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${validationResults.reportingAggregateNoErrors ? "bg-emerald-100" : "bg-red-100"}`}>
                                {validationResults.reportingAggregateNoErrors ? (
                                  <svg className="w-3 h-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                ) : (
                                  <span className="text-red-600 text-xs font-bold">✕</span>
                                )}
                              </span>
                              <div className="flex-1 min-w-0">
                                <span className={`text-sm font-medium ${validationResults.reportingAggregateNoErrors ? "text-slate-900" : "text-slate-700"}`}>No errors in REPORTING_AGGREGATE</span>
                              </div>
                              <span className={`flex-shrink-0 text-xs font-bold ${validationResults.reportingAggregateNoErrors ? "text-emerald-600" : "text-red-600"}`}>
                                {validationResults.reportingAggregateNoErrors ? "✓" : "✗"}
                              </span>
                            </li>
                          )}
                          {animatedItemsCount >= 8 && (
                            <li className="flex items-start gap-3 animate-fade-in animate-slide-in-left p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                              <span className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${validationResults.reportingByproductNoErrors ? "bg-emerald-100" : "bg-red-100"}`}>
                                {validationResults.reportingByproductNoErrors ? (
                                  <svg className="w-3 h-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                ) : (
                                  <span className="text-red-600 text-xs font-bold">✕</span>
                                )}
                              </span>
                              <div className="flex-1 min-w-0">
                                <span className={`text-sm font-medium ${validationResults.reportingByproductNoErrors ? "text-slate-900" : "text-slate-700"}`}>No errors in REPORTING_BYPRODUCT</span>
                              </div>
                              <span className={`flex-shrink-0 text-xs font-bold ${validationResults.reportingByproductNoErrors ? "text-emerald-600" : "text-red-600"}`}>
                                {validationResults.reportingByproductNoErrors ? "✓" : "✗"}
                              </span>
                            </li>
                          )}
                        </ul>
                      </div>
                    </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {loadingExistingFile ? (
              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6 flex items-center justify-center">
                <svg className="w-5 h-5 animate-spin text-blue-600 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm text-blue-700 font-medium">Checking for existing file...</span>
              </div>
            ) : existingFile ? (
              <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <svg className="w-5 h-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8 16.5a.5.5 0 01.5-.5h3a.5.5 0 01.5.5v2a.5.5 0 01-.5.5h-3a.5.5 0 01-.5-.5v-2zm-3-1a.5.5 0 01.5-.5h9a.5.5 0 01.5.5v1a.5.5 0 01-.5.5h-9a.5.5 0 01-.5-.5v-1zm0-4a.5.5 0 01.5-.5h9a.5.5 0 011 0v1a.5.5 0 01-1 0h-9a.5.5 0 01-.5-.5v-1zm0-3a.5.5 0 01.5-.5h.75a.75.75 0 000-1.5h-.75a1.5 1.5 0 00-1.5 1.5v6a1.5 1.5 0 001.5 1.5h9a1.5 1.5 0 001.5-1.5v-6a1.5 1.5 0 00-1.5-1.5h-.75a.75.75 0 000 1.5h.75a.5.5 0 01.5.5v6a.5.5 0 01-.5.5h-9a.5.5 0 01-.5-.5v-6z" clipRule="evenodd" />
                  </svg>
                  <h3 className="text-sm font-bold text-blue-900">Existing File Found</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-900">{existingFile.filename}</p>
                      {existingFile.fileSize && (
                        <p className="text-xs text-blue-700 mt-1">
                          {(existingFile.fileSize / 1024).toFixed(2)} KB
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleDownloadFile}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 px-4 py-2 font-medium text-white shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={deletingFile}
                    >
                      <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download
                    </button>
                    <button
                      onClick={handleDeleteFile}
                      disabled={deletingFile}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 px-4 py-2 font-medium text-white shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deletingFile ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {/* PATHWAYS Selection Table Preview Modal */}
            {showPathwaysPreview && validationResults?.pathwaysTableJson && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in">
                <div className="w-full max-w-6xl rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-8 py-6 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-6 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full"></div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900">PATHWAYS Selection Table</h3>
                        <p className="text-sm text-slate-600 mt-1">{validationResults.pathwaysTableJson.length} rows</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPathwaysPreview(false)}
                      className="inline-flex items-center justify-center w-10 h-10 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-all"
                    >
                      <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="overflow-auto flex-1">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-gradient-to-r from-slate-100 to-slate-200 z-10 border-b border-slate-200">
                        <tr>
                          {validationResults.pathwaysTableJson[0] &&
                            Object.keys(validationResults.pathwaysTableJson[0]).map((key) => (
                              <th
                                key={key}
                                className="border-r border-slate-200 px-4 py-3 text-left font-bold text-slate-700 whitespace-nowrap"
                              >
                                {key}
                              </th>
                            ))}
                        </tr>
                      </thead>
                      <tbody>
                        {validationResults.pathwaysTableJson.map((row, i) => (
                          <tr
                            key={i}
                            className={`border-b border-slate-200 hover:bg-purple-50/40 transition-colors ${
                              i % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                            }`}
                          >
                            {Object.values(row).map((value, j) => (
                              <td key={j} className="border-r border-slate-200 px-4 py-2 text-slate-700">
                                {value !== null && value !== undefined ? String(value).substring(0, 100) : "—"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
